import { z } from 'zod';
import { DomainError } from '../errors.js';
import {
  folderCodeKey,
  MAX_NODE_KEY_LENGTH,
  officeNodeKey,
  TEMPLATE_SECTIONS,
} from './template.js';
import {
  compareTreeNodes,
  type TreeNode,
  type TreeNodeFields,
  type TreeNodeUpdate,
} from './tree-plan.js';

/**
 * Ofisin gemi ağacı işlemleri (ADR 0037 §11; ADR 0038 §8): ekle, yeniden adlandır, sırala, taşı,
 * emekliye ayır ve geri al. Silme yoktur [NR496 4.3.2a]. İşlem tek gemiye, seçili gemilere ya da
 * bütün gemilere uygulanır; düğüm her gemide anahtarla bulunur ve plan gemi başına çıkarılır.
 * Dokunulan her düğüm "ofis değiştirdi" izini (`officeModifiedAt`) işlemin zamanına çeker; şablon
 * güncellemeleri bu düğümleri ezmez (ADR 0038 §6). Yetki (`document_tree.edit`), kilit ve
 * "bir gemide hata varsa hiçbirine" kuralı yazma katmanındadır.
 */

const text = (min: number, max: number) => z.string().trim().min(min).max(max);
const nodeKey = z.string().min(1).max(MAX_NODE_KEY_LENGTH);
/** Türkçe ad boş bırakılabilir (`null`); gösterimde İngilizce ad kullanılır. */
const nameSchema = z.strictObject({ en: text(1, 120), tr: text(1, 120).nullable() });

export const OFFICE_TREE_OPERATIONS = [
  'add',
  'rename',
  'reorder',
  'move',
  'retire',
  'restore',
  'remove',
] as const;
export type OfficeTreeOperationType = (typeof OFFICE_TREE_OPERATIONS)[number];

/** Ofis ağaç işlemi; gemi, bulut ve arayüz aynı şemayı kullanır. */
export const officeTreeOperationSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('add'),
    section: z.enum(TEMPLATE_SECTIONS),
    parentKey: nodeKey.nullable(),
    code: text(1, 32),
    name: nameSchema,
  }),
  z.strictObject({ type: z.literal('rename'), key: nodeKey, code: text(1, 32), name: nameSchema }),
  z.strictObject({ type: z.literal('reorder'), key: nodeKey, direction: z.enum(['up', 'down']) }),
  z.strictObject({ type: z.literal('move'), key: nodeKey, parentKey: nodeKey.nullable() }),
  z.strictObject({ type: z.literal('retire'), key: nodeKey }),
  z.strictObject({ type: z.literal('restore'), key: nodeKey }),
  // Kurulumdaki gemide ofisin açtığı boş klasör (ADR 0142 §4)
  z.strictObject({ type: z.literal('remove'), key: nodeKey }),
]);
export type OfficeTreeOperation = z.output<typeof officeTreeOperationSchema>;
export type OfficeTreeOperationInput = z.input<typeof officeTreeOperationSchema>;

export interface OfficeTreeContext {
  /** Eklenen klasörün anahtar UUID'si (UUIDv7): işlem başına bir kez, bütün gemilerde aynı. */
  newKey: string;
  /** Satır kimliği üreteci (gemi başına ayrı satır). */
  newId: () => string;
  /** İşlemin zamanı (ISO-8601 UTC); iz ve emeklilik zamanı. */
  now: string;
}

/**
 * Gemi başına sonuç: anahtar (ya da hedef üst) o gemide yoksa `not_present` (hata değildir;
 * "uygulanmadı: yok"), yazılacak bir şey yoksa `unchanged`.
 */
export type OfficeTreeResult =
  | { status: 'not_present' }
  | { status: 'unchanged' }
  | {
      status: 'applied';
      inserts: TreeNode[];
      updates: TreeNodeUpdate[];
      /** Soft delete edilecek düğümler (yalnız `remove`; ADR 0142 §4). */
      removes?: string[];
    };

const NOT_PRESENT: OfficeTreeResult = { status: 'not_present' };
const UNCHANGED: OfficeTreeResult = { status: 'unchanged' };

/**
 * Bir ofis işleminin bu gemi ağacındaki planı. Kurallar (ADR 0038 §8, §9):
 * - ekleme `<bölüm>.office-<uuid>` anahtarıyla, `origin: 'office'`, kardeşlerin sonuna;
 * - kardeşler arasında canlı kod tekil (`DUPLICATE_FOLDER_CODE`);
 * - sıralama komşu iki düğümün yerini değiştirir; ikisi de iz alır;
 * - taşıma aynı bölümde (`FOLDER_SECTION_MISMATCH`), döngüsüz (`FOLDER_CYCLE`), canlı üste
 *   (`FOLDER_RETIRED`); yeni kardeşlerin sonuna;
 * - emekli klasör salt okunurdur: ad, sıra ve yer değişmez (`FOLDER_RETIRED`);
 * - emekliye ayırma alt düğümleriyle alttan üste (`retireReason: 'office'`);
 * - geri alma yalnız üstü canlı düğümde (`FOLDER_PARENT_RETIRED`); düğüm ve aynı işlemle
 *   emekliye ayrılmış altları geri gelir;
 * - kaldırma (ADR 0142 §4) yalnız ofisin açtığı klasörde (`FOLDER_DELETE_FORBIDDEN`; şablon
 *   düğümü emekliye ayrılır) ve altında klasör yokken (emekli dahil; `FOLDER_NOT_EMPTY`). Geminin
 *   kurulumda olması ve klasörde belge olmaması yazma katmanında denetlenir.
 */
export function planOfficeTreeOperation(
  input: OfficeTreeOperationInput,
  nodes: readonly TreeNode[],
  context: OfficeTreeContext,
): OfficeTreeResult {
  const parsed = officeTreeOperationSchema.safeParse(input);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const path = issue?.path.map(String).join('.') ?? '';
    throw new DomainError('VALIDATION_FAILED', `office tree operation: ${path}`, path);
  }
  const op = parsed.data;
  const tree = new TreeIndex(nodes);
  const touch = { officeModifiedAt: context.now };

  if (op.type === 'add') {
    const key = officeNodeKey(op.section, context.newKey);
    const parent = op.parentKey === null ? null : tree.byKey.get(op.parentKey);
    if (parent === undefined) return NOT_PRESENT;
    if (parent && parent.section !== op.section) {
      throw new DomainError('FOLDER_SECTION_MISMATCH', op.parentKey ?? '');
    }
    if (parent && parent.retiredAt !== null)
      throw new DomainError('FOLDER_RETIRED', parent.key ?? '');
    if (tree.byKey.has(key)) throw new DomainError('DUPLICATE_KEY', key, key);
    const parentId = parent?.id ?? null;
    tree.assertCodeFree(op.section, parentId, op.code);
    return {
      status: 'applied',
      inserts: [
        {
          id: context.newId(),
          key,
          origin: 'office',
          parentId,
          section: op.section,
          code: op.code,
          name: op.name.en,
          nameTr: op.name.tr,
          sortOrder: tree.nextOrder(op.section, parentId),
          templateVersion: null,
          officeModifiedAt: context.now,
          retiredAt: null,
          retireReason: null,
        },
      ],
      updates: [],
    };
  }

  const node = tree.byKey.get(op.key);
  if (!node) return NOT_PRESENT;
  const update = (target: TreeNode, set: Partial<TreeNodeFields>): TreeNodeUpdate => ({
    id: target.id,
    key: target.key,
    set: { ...set, ...touch },
  });
  const applied = (updates: TreeNodeUpdate[]): OfficeTreeResult => ({
    status: 'applied',
    inserts: [],
    updates,
  });

  switch (op.type) {
    case 'rename': {
      assertLive(node);
      const set: Partial<TreeNodeFields> = {};
      if (op.code !== node.code) set.code = op.code;
      if (op.name.en !== node.name) set.name = op.name.en;
      if (op.name.tr !== node.nameTr) set.nameTr = op.name.tr;
      if (Object.keys(set).length === 0) return UNCHANGED;
      if (set.code !== undefined)
        tree.assertCodeFree(node.section, node.parentId, op.code, node.id);
      return applied([update(node, set)]);
    }
    case 'reorder': {
      assertLive(node);
      const siblings = tree.liveChildren(node.section, node.parentId);
      const at = siblings.findIndex((s) => s.id === node.id);
      const neighbor = siblings[op.direction === 'up' ? at - 1 : at + 1];
      if (!neighbor) return UNCHANGED;
      const desired = siblings.map((s) => s.id);
      desired[siblings.indexOf(neighbor)] = node.id;
      desired[at] = neighbor.id;
      // Önce iki sıranın yer değiştirmesi; eşit sıralarda ya da başka kardeşle eşitlikte
      // yetmezse kardeşler en küçük sıradan başlayarak yeniden numaralanır
      const swapped = new Map([
        [node.id, neighbor.sortOrder],
        [neighbor.id, node.sortOrder],
      ]);
      const after = siblings
        .map((s) => ({ ...s, sortOrder: swapped.get(s.id) ?? s.sortOrder }))
        .sort(compareTreeNodes)
        .map((s) => s.id);
      if (after.every((id, i) => id === desired[i])) {
        return applied([
          update(node, { sortOrder: neighbor.sortOrder }),
          update(neighbor, { sortOrder: node.sortOrder }),
        ]);
      }
      const base = Math.min(...siblings.map((s) => s.sortOrder));
      const orderOf = new Map(desired.map((id, i) => [id, base + i]));
      const others = siblings.filter(
        (s) => s.id !== node.id && s.id !== neighbor.id && orderOf.get(s.id) !== s.sortOrder,
      );
      return applied(
        [node, neighbor, ...others].map((s) => update(s, { sortOrder: orderOf.get(s.id) ?? 0 })),
      );
    }
    case 'move': {
      assertLive(node);
      const parent = op.parentKey === null ? null : tree.byKey.get(op.parentKey);
      if (parent === undefined) return NOT_PRESENT;
      if (parent) {
        if (parent.section !== node.section) {
          throw new DomainError('FOLDER_SECTION_MISMATCH', op.parentKey ?? '');
        }
        if (parent.retiredAt !== null) throw new DomainError('FOLDER_RETIRED', parent.key ?? '');
        if (parent.id === node.id || tree.isDescendant(parent.id, node.id)) {
          throw new DomainError('FOLDER_CYCLE', op.key);
        }
      }
      const parentId = parent?.id ?? null;
      if (parentId === node.parentId) return UNCHANGED;
      tree.assertCodeFree(node.section, parentId, node.code, node.id);
      return applied([
        update(node, { parentId, sortOrder: tree.nextOrder(node.section, parentId) }),
      ]);
    }
    case 'retire': {
      if (node.retiredAt !== null) return UNCHANGED;
      // Alttan üste: önce altlar (kardeş sırasıyla), sonra düğüm
      const order: TreeNode[] = [];
      const visit = (current: TreeNode) => {
        for (const child of tree.liveChildren(current.section, current.id)) visit(child);
        order.push(current);
      };
      visit(node);
      return applied(
        order.map((n) => update(n, { retiredAt: context.now, retireReason: 'office' })),
      );
    }
    case 'remove': {
      if (node.origin !== 'office') throw new DomainError('FOLDER_DELETE_FORBIDDEN', node.id);
      if (tree.children(node.id).length > 0) throw new DomainError('FOLDER_NOT_EMPTY', node.id);
      return { status: 'applied', inserts: [], updates: [], removes: [node.id] };
    }
    case 'restore': {
      if (node.retiredAt === null) return UNCHANGED;
      const parent = node.parentId === null ? undefined : tree.byId.get(node.parentId);
      if (parent && parent.retiredAt !== null) {
        throw new DomainError('FOLDER_PARENT_RETIRED', node.key ?? '');
      }
      // Şablonun emekliye ayırdığı düğüm de geri alınabilir; iz aldığı için şablon onu yeniden
      // emekliye ayırmaz (ADR 0038 §6). Altlar: aynı anda, aynı nedenle emekliye ayrılanlar
      const restored: TreeNode[] = [];
      const visit = (current: TreeNode) => {
        restored.push(current);
        for (const child of tree.children(current.id)) {
          if (child.retiredAt === node.retiredAt && child.retireReason === node.retireReason) {
            visit(child);
          }
        }
      };
      visit(node);
      const back = new Set(restored.map((n) => n.id));
      for (const n of restored) tree.assertCodeFree(n.section, n.parentId, n.code, n.id, back);
      return applied(restored.map((n) => update(n, { retiredAt: null, retireReason: null })));
    }
  }
}

function assertLive(node: TreeNode): void {
  // Emekli klasör salt okunurdur (ADR 0038 §9)
  if (node.retiredAt !== null) throw new DomainError('FOLDER_RETIRED', node.key ?? node.id);
}

/** Ağacın dizinleri ve yardımcı denetimler. */
class TreeIndex {
  readonly byId = new Map<string, TreeNode>();
  readonly byKey = new Map<string, TreeNode>();
  readonly #children = new Map<string | null, TreeNode[]>();

  constructor(nodes: readonly TreeNode[]) {
    for (const node of nodes) {
      this.byId.set(node.id, node);
      if (node.key !== null) this.byKey.set(node.key, node);
      const list = this.#children.get(node.parentId) ?? [];
      list.push(node);
      this.#children.set(node.parentId, list);
    }
    for (const list of this.#children.values()) list.sort(compareTreeNodes);
  }

  /** Bütün altlar (kökte bütün bölümler), kardeş sırasıyla. */
  children(parentId: string | null): TreeNode[] {
    return this.#children.get(parentId) ?? [];
  }

  /** Bölümdeki canlı kardeşler, kardeş sırasıyla. */
  liveChildren(section: TreeNode['section'], parentId: string | null): TreeNode[] {
    return this.children(parentId).filter((n) => n.section === section && n.retiredAt === null);
  }

  /** Kardeşlerin sonu: canlı kardeşlerin en büyük sırasından bir fazla. */
  nextOrder(section: TreeNode['section'], parentId: string | null): number {
    return Math.max(0, ...this.liveChildren(section, parentId).map((n) => n.sortOrder)) + 1;
  }

  /** `candidateId` düğümü `ancestorId`'nin altında mı. */
  isDescendant(candidateId: string, ancestorId: string): boolean {
    const seen = new Set<string>();
    let cursor = this.byId.get(candidateId)?.parentId ?? null;
    while (cursor !== null && !seen.has(cursor)) {
      if (cursor === ancestorId) return true;
      seen.add(cursor);
      cursor = this.byId.get(cursor)?.parentId ?? null;
    }
    return false;
  }

  /**
   * Canlı kardeşler arasında kod tekil (kırpılmış, büyük-küçük harf duyarsız). `alsoLive`: aynı
   * işlemle canlanacak düğümler (geri alma).
   */
  assertCodeFree(
    section: TreeNode['section'],
    parentId: string | null,
    code: string,
    exceptId?: string,
    alsoLive: ReadonlySet<string> = new Set(),
  ): void {
    const wanted = folderCodeKey(code);
    const clash = this.children(parentId).some(
      (n) =>
        n.id !== exceptId &&
        n.section === section &&
        (n.retiredAt === null || alsoLive.has(n.id)) &&
        folderCodeKey(n.code) === wanted,
    );
    if (clash) throw new DomainError('DUPLICATE_FOLDER_CODE', code);
  }
}
